import type { PartnerOrganization } from "@entities";
import { Autocomplete, TextField } from "@mui/material";
import { partnerOrganizationRepo } from "@repositories";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";

type Props = {
  value: PartnerOrganization | null;
  onChange: (org: PartnerOrganization | null) => void;
  label?: string;
  size?: "small" | "medium";
  disabled?: boolean;
  /** Preload all orgs (for a single dialog/filter). Default: search-as-you-type */
  preloadAll?: boolean;
  placeholder?: string;
};

export const PartnerOrganizationSelect: FC<Props> = ({
  value,
  onChange,
  label = "ארגון שותף",
  size = "small",
  disabled = false,
  preloadAll = false,
  placeholder,
}) => {
  const [options, setOptions] = useState<PartnerOrganization[]>([]);
  const [inputValue, setInputValue] = useState(value?.name ?? "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setInputValue(value?.name ?? "");
  }, [value?.id, value?.name]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const list = await partnerOrganizationRepo.find({
        orderBy: { name: "asc" },
        limit: 200,
      });
      setOptions(list);
    } finally {
      setLoading(false);
    }
  }, []);

  const search = useCallback(async (query: string) => {
    setLoading(true);
    try {
      const list = await partnerOrganizationRepo.find({
        where: { name: { $contains: query } },
        orderBy: { name: "asc" },
        limit: 30,
      });
      setOptions(list);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (preloadAll) {
      void loadAll();
    }
  }, [preloadAll, loadAll]);

  useEffect(() => {
    if (preloadAll) return;
    const q = inputValue.trim();
    if (!q || q === (value?.name ?? "")) return;
    const handle = window.setTimeout(() => {
      void search(q);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [inputValue, preloadAll, search, value?.name]);

  const mergedOptions = useMemo(() => {
    if (!value) return options;
    if (options.some((o) => o.id === value.id)) return options;
    return [value, ...options];
  }, [options, value]);

  return (
    <Autocomplete
      options={mergedOptions}
      value={value}
      onChange={(_, v) => onChange(v)}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      loading={loading}
      disabled={disabled}
      noOptionsText={
        preloadAll ? "אין ארגונים" : "הקלד לחיפוש ארגון שותף"
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size={size}
          placeholder={placeholder}
        />
      )}
    />
  );
};
